import React, {
  FunctionComponent,
  PropsWithChildren,
  ReactNode,
  useRef,
  useState,
} from "react";
import {
  CardCvvElement,
  CardMonthElement,
  CardNumberElement,
  CardYearElement,
  useRecurly,
} from "@recurly/react-recurly";
import clsx from "clsx";
import { useTranslation } from "react-i18next";
import Button from "../../../../../components/button/button";
import { FormControl, Input, InputLabel, NativeSelect } from "@mui/material";
import countries from "../../../../../core/consts/countries";
import {
  convertRecurlyErrors,
  SUBSCRIPTION_FORM_FIELDS,
  SubscriptionFormValues,
  useSubscriptionFormik,
} from "../formik/formik";
import FormHelperText from "@mui/material/FormHelperText";
import RecurlyWrapper from "../../../../../components/recurly-wrapper/recurly-wrapper";
import styles from "./recurly-form.module.scss";
import { useDI } from "../../../../../core/di";
import ApiSubmission from "./api-submission";
import { Form } from "../type";
import Event from "../../../../../core/consts/analytics-events";

const FormFieldLabel = (props: { text: string }) => {
  return (
    <InputLabel
      shrink={true}
      error={false}
      classes={{
        focused: styles.Focused,
      }}
    >
      {props.text}
    </InputLabel>
  );
};

const FormControlWrapper = (
  props: PropsWithChildren<{
    errors: ReactNode;
    label: string;
    testid: string;
  }>
) => {
  const { errors, label } = props;
  return (
    <FormControl error={!!errors}>
      <FormFieldLabel text={label} />
      {props.children}
      {errors && (
        <FormHelperText
          sx={{ marginLeft: 0 }}
          data-testid={`error-msg-${props.testid}`}
        >
          {errors}
        </FormHelperText>
      )}
    </FormControl>
  );
};

export function InnerForm(props: Form) {
  const {
    status,
    introText,
    introComponent,
    subscriptionId,
    onSuccess,
    onError,
  } = props;
  const [loading, setLoading] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  const recurly = useRecurly();
  const formik = useSubscriptionFormik();
  const { t } = useTranslation();
  const { recurlyApi, analytics, nexarApi, logger } = useDI();

  const handleSubmit = async (event: any) => {
    // Stop the form submission native behavior
    event.preventDefault();

    // Animate the loader
    setLoading(true);

    // Validate the form
    let formikErrors = await formik.validateForm();

    // Prepare the variables to collect data from Recurly
    let recurlyErrors = {};
    let recurlyTokenId: string;

    try {
      // Call recurly, get token id or validation errors
      const recurlyAnswer = await recurlyApi.getRecurlyPaymentToken(
        recurly,
        formRef.current
      );
      recurlyTokenId = recurlyAnswer.tokenId;
      recurlyErrors = convertRecurlyErrors(recurlyAnswer.details);
    } catch (error) {
      const mixpanelEvent =
        status === "ACTIVE"
          ? Event.ConnectEditFailed
          : Event.ConnectActivateFailed;

      analytics?.track(mixpanelEvent, { subscriptionId, error });
      logger?.Error(new Error("Failed to get recurly.js token"), {
        subscription: {
          id: subscriptionId,
          status: status,
          formValues: formik.values,
        },
        error,
      });
      return onError(error);
    }

    // Merge the errors, formik takes precedence
    const allErrors = { ...recurlyErrors, ...formikErrors };

    const thereAreErrors = Object.keys(allErrors).length > 0;
    if (thereAreErrors) {
      // Show the errors
      formik.setErrors(allErrors);
    } else {
      // Continue submitting the form
      ApiSubmission(
        nexarApi,
        analytics,
        subscriptionId,
        formik.values,
        recurlyTokenId,
        status
      )
        .then(() => {
          return onSuccess();
        })
        .catch((error) => {
          const mixpanelEvent =
            status === "ACTIVE"
              ? Event.ConnectEditFailed
              : Event.ConnectActivateFailed;

          analytics?.track(mixpanelEvent, { subscriptionId, error });

          logger?.Error(new Error("Failed to handle subscription action"), {
            subscription: {
              id: subscriptionId,
              status: status,
              tokenId: recurlyTokenId,
              formValues: formik.values,
            },
            error,
          });
          return onError(error);
        });
    }
    // Stop loader animation
    setLoading(false);
  };

  const intro = () => {
    return (
      <div className={styles.Intro}>
        {introText ? <p>{introText}</p> : introComponent}
      </div>
    );
  };

  const renderFormControl = (
    fieldObj: { key: keyof SubscriptionFormValues; recurlyData: string },
    inputProps?: any
  ) => {
    const { key, recurlyData } = fieldObj;
    const { inputComponent, defaultValue, ...otherInputProps } = inputProps || {
      inputComponent: "input",
    };
    const fieldErrors = formik.errors[key];

    const style = {
      input: { fontSize: "16px" },
      "&::before": { borderColor: "#e4e5e7" },
    };

    return (
      <FormControlWrapper errors={fieldErrors} label={t(key)} testid={key}>
        <Input
          value={formik.values?.[key]}
          name={key}
          onChange={formik.handleChange}
          sx={style}
          inputComponent={inputComponent}
          defaultValue={defaultValue}
          inputProps={{
            "data-recurly": recurlyData,
            "data-testid": `subscription-form-input-${key}`,
            ...otherInputProps,
          }}
        />
      </FormControlWrapper>
    );
  };
  const renderCountriesControl = () => {
    const { key, recurlyData } = SUBSCRIPTION_FORM_FIELDS.COUNTRY as {
      key: "country";
      recurlyData: string;
    }; // We need to guarantee the key is of the right type, not just "string"
    const fieldErrors = formik.errors[key];

    const countryList = countries.sort().map((k: any) => (
      <option key={k.alpha2} value={k.alpha2}>
        {k.name}
      </option>
    ));

    return (
      <FormControlWrapper errors={fieldErrors} label={t(key)} testid={key}>
        <NativeSelect
          name={key}
          value={formik.values[key]}
          onChange={formik.handleChange}
          inputProps={{
            //@ts-ignore
            "data-recurly": recurlyData,
            //@ts-ignore
            "data-testid": `subscription-form-input-${key}`,
          }}
        >
          {countryList}
        </NativeSelect>
      </FormControlWrapper>
    );
  };

  const renderRecurlyCardControl = (
    fieldObj: { key: keyof SubscriptionFormValues; recurlyData: string },
    component: FunctionComponent<any>,
    placeholder?: string
  ) => {
    const { key } = fieldObj;
    const fieldErrors = formik.errors[key];
    const Component = component;

    const componentClassNames = clsx(
      styles.CardElement,
      fieldErrors && styles.Invalid
    );

    const style: any = {};
    if (placeholder) {
      style.placeholder = {
        content: placeholder,
      };
    }
    style.fontWeight = "600";

    return (
      <FormControlWrapper
        errors={fieldErrors}
        label={t(key)}
        testid={fieldObj.key}
      >
        <Component className={componentClassNames} style={style} />
      </FormControlWrapper>
    );
  };

  const renderRecurlyExpirationCardControl = () => {
    const fieldErrors =
      formik.errors[SUBSCRIPTION_FORM_FIELDS.CARD_EXP_MONTH.key] ||
      formik.errors[SUBSCRIPTION_FORM_FIELDS.CARD_EXP_YEAR.key];

    const componentClassNames = clsx(
      styles.CardExpirationWrapper,
      fieldErrors && styles.Invalid
    );

    return (
      <FormControlWrapper
        errors={fieldErrors}
        label={t("expirationDate")}
        testid={"expirationDate"}
      >
        <div className={componentClassNames}>
          <CardMonthElement
            style={{ placeholder: { content: "MM" }, fontWeight: "600" }}
          />
          <CardYearElement
            style={{ placeholder: { content: "YY" }, fontWeight: "600" }}
          />
        </div>
      </FormControlWrapper>
    );
  };

  const renderForm = () => {
    return (
      <React.Fragment>
        <div className={styles.Fields}>
          {renderFormControl(SUBSCRIPTION_FORM_FIELDS.FIRST_NAME)}
          {renderFormControl(SUBSCRIPTION_FORM_FIELDS.LAST_NAME)}
          {renderFormControl(SUBSCRIPTION_FORM_FIELDS.EMAIL)}
          {renderRecurlyCardControl(
            SUBSCRIPTION_FORM_FIELDS.CARD_NUMBER,
            CardNumberElement
          )}
          {renderRecurlyExpirationCardControl()}
          {renderRecurlyCardControl(
            SUBSCRIPTION_FORM_FIELDS.CVV,
            CardCvvElement,
            "3 digits"
          )}
          {renderCountriesControl()}
          {renderFormControl(SUBSCRIPTION_FORM_FIELDS.ADDRESS)}
          {renderFormControl(SUBSCRIPTION_FORM_FIELDS.CITY)}
          {renderFormControl(SUBSCRIPTION_FORM_FIELDS.STATE)}
          {renderFormControl(SUBSCRIPTION_FORM_FIELDS.ZIP)}
        </div>
        <footer className={styles.Footer}>{footer()}</footer>
      </React.Fragment>
    );
  };

  const footer = () => {
    return (
      <footer>
        <Button
          submit={true}
          loading={loading}
          data-testid={"subscription-form-submit"}
        >
          {props.btnText}
        </Button>
        <p>{props.footerParagraph}</p>
      </footer>
    );
  };

  return (
    <div className={styles.FormWrapper}>
      {intro()}
      <form action="#" onSubmit={handleSubmit} ref={formRef}>
        <h2>{t("paymentDetails")}</h2>
        {renderForm()}
      </form>
    </div>
  );
}

function MyNexarRecurlyForm(props: Form) {
  return (
    <RecurlyWrapper>
      <InnerForm {...props} />
    </RecurlyWrapper>
  );
}

export default MyNexarRecurlyForm;
